// src/pages/admin/ItemList.js
import React, { useEffect, useState, useRef } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import { Button } from "@mui/material";
import { Breadcrumb } from "../../../components";
import { CardHeader } from "../../../components/cards";
import { Box } from "../../../components/elements";
import PageLayout from "../../../layouts/PageLayout";
import { DataGrid } from "@mui/x-data-grid";
import { Modal, ModalClose, Sheet, Typography } from "@mui/joy";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

export default function ItemList() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // Toggle between "storehouse" and "master" views
  const [view, setView] = useState("storehouse");

  // Refs for hidden file inputs
  const storeFileRef = useRef(null);
  const masterFileRef = useRef(null);

  // Data state
  const [storeItems, setStoreItems]   = useState([]);
  const [masterItems, setMasterItems] = useState([]);
  const [callApi, setCallApi]         = useState(false);

  // Confirmation modal state
  const [warning, setWarning]     = useState({ text: "", type: "", id: null });
  const [openWarning, setOpenWarning] = useState(false);

  // Fetch both lists whenever callApi toggles
useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };

    // Storehouse-specific items
    axios
      .get(`${process.env.REACT_APP_BASE_URL}common/items`, { headers })
      .then((r) => {
        console.log('Storehouse items loaded:', r.data.length);
        setStoreItems(r.data);
      })
      .catch((err) => {
        console.error('Error loading storehouse items:', err);
        alert("Session expired");
        localStorage.clear();
        navigate("/login");
      });

    // Master items
    axios
      .get(`${process.env.REACT_APP_BASE_URL}common/master-items`, { headers })
      .then((r) => {
        console.log('Master items loaded:', r.data.length);
        setMasterItems(r.data);
      })
      .catch((err) => {
        console.error('Error loading master items:', err);
      });
  }, [token, callApi, navigate]);

  //
  // Storehouse-item handlers
  //
  const deleteStoreItem = async (id) => {
    await axios.delete(
      `${process.env.REACT_APP_BASE_URL}common/items/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCallApi((c) => !c);
  };
  const toggleStoreStatus = async (id) => {
    const it = storeItems.find((x) => x._id === id);
    await axios.put(
      `${process.env.REACT_APP_BASE_URL}common/items/${id}`,
      { active: !it.active },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCallApi((c) => !c);
  };
  const onStoreFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}common/bulk-upload/storehouse-items`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          maxBodyLength: Infinity,
        }
      );
      alert("Storehouse bulk upload successful");
      setCallApi((c) => !c);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Upload failed");
    }
  };

  //
  // Master-item handlers
  //
  const deleteMasterItem = async (id) => {
    await axios.delete(
      `${process.env.REACT_APP_BASE_URL}common/master-items/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCallApi((c) => !c);
  };
  const onMasterFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}common/bulk-upload/master-items`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          maxBodyLength: Infinity,
        }
      );
      alert("Master bulk upload successful");
      setCallApi((c) => !c);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Upload failed");
    }
  };

  //
  // Define columns & rows for MUI DataGrid format
  //
  const storehouseColumns = [
    { 
      field: "s_no", 
      headerName: "S.no", 
      width: 60,
      editable: false 
    },
    { 
      field: "storehouse", 
      headerName: "Storehouse", 
      width: 100,
      editable: false 
    },
    { 
      field: "pn", 
      headerName: "Item PN", 
      width: 120,
      editable: false 
    },
    { 
      field: "name", 
      headerName: "Name", 
      width: 200,
      editable: true  // ✅ Inline editing enabled
    },
    { 
      field: "locId", 
      headerName: "LocID", 
      width: 80,
      editable: false 
    },
    { 
      field: "vendor", 
      headerName: "Vendor", 
      width: 120,
      editable: true  // ✅ Inline editing enabled
    },
    { 
      field: "easPN", 
      headerName: "EAS PN", 
      width: 120,
      editable: true  // ✅ Inline editing enabled
    },
    { 
      field: "min", 
      headerName: "Min", 
      width: 60,
      type: 'number',
      editable: true  // ✅ Inline editing enabled
    },
    { 
      field: "max", 
      headerName: "Max", 
      width: 60,
      type: 'number',
      editable: true  // ✅ Inline editing enabled
    },
    { 
      field: "orderMult", 
      headerName: "Order Mult.", 
      width: 80,
      type: 'number',
      editable: true  // ✅ Inline editing enabled
    },
    { 
      field: "group", 
      headerName: "Group", 
      width: 100,
      editable: true  // ✅ Inline editing enabled
    },
    { 
      field: "alwaysCount", 
      headerName: "Always Count", 
      width: 120,
      type: 'boolean',
      editable: true  // ✅ Inline editing enabled
    },
    { 
      field: "shelf", 
      headerName: "Shelf", 
      width: 80,
      editable: true  // ✅ Inline editing enabled
    },
    { 
      field: "active", 
      headerName: "Active", 
      width: 80,
      type: 'boolean',
      editable: true  // ✅ Inline editing enabled
    },
    { 
      field: "actions", 
      headerName: "Actions", 
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => navigate(`/edit-item/${params.row.id}`)}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={() => {
              setWarning({
                text: "Delete this storehouse item?",
                type: "deleteStore",
                id: params.row.id,
              });
              setOpenWarning(true);
            }}
          >
            Delete
          </Button>
        </div>
      )
    },
  ];

  const storehouseRows = storeItems.map((it, idx) => ({
    id: it._id,  // MUI DataGrid requires 'id' field
    s_no: idx + 1,
    storehouse: it.storehouse.code,
    pn: it.itemPN,
    name: it.itemName,
    locId: it.locId,
    vendor: it.vendor || "",
    easPN: it.easPN || "",
    min: it.min ?? 0,
    max: it.max ?? 0,
    orderMult: it.orderMult ?? 1,
    group: it.group || "",
    alwaysCount: it.alwaysCount || false,
    shelf: it.shelf || "",
    active: it.active !== undefined ? it.active : true,
  }));

const masterColumns = [
    { 
      field: "s_no", 
      headerName: "S.no", 
      width: 60,
      editable: false 
    },
    { 
      field: "pn", 
      headerName: "Item PN", 
      width: 120,
      editable: false 
    },
    { 
      field: "description", 
      headerName: "Description", 
      width: 200,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "issueMult", 
      headerName: "Issue Mult", 
      width: 80,
      type: 'number',
      editable: true  // ✅ Inline editing
    },
    { 
      field: "uom", 
      headerName: "UOM", 
      width: 80,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "costEach", 
      headerName: "Cost Each", 
      width: 100,
      type: 'number',
      editable: true  // ✅ Inline editing
    },
    { 
      field: "convFactor", 
      headerName: "Conv", 
      width: 60,
      type: 'number',
      editable: true  // ✅ Inline editing
    },
    { 
      field: "vendorPN", 
      headerName: "Vendor PN", 
      width: 120,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "expires", 
      headerName: "Expires", 
      width: 100,
      type: 'date',
      editable: true,  // ✅ Inline editing
      valueGetter: (params) => params.value ? new Date(params.value) : null,
    },
    { 
      field: "procNotes", 
      headerName: "Proc Notes", 
      width: 150,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "commodityCode", 
      headerName: "Commodity Code", 
      width: 120,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "defaultCategory", 
      headerName: "Default Category", 
      width: 120,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "ccGL", 
      headerName: "CCGL", 
      width: 80,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "ccJob", 
      headerName: "CC_JOB", 
      width: 80,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "ccWty", 
      headerName: "CC_WTY", 
      width: 80,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "ccWo", 
      headerName: "CC_WO", 
      width: 80,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "ccCapex", 
      headerName: "CC_CAPEX", 
      width: 80,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "glBm", 
      headerName: "GL_BM", 
      width: 80,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "glEm", 
      headerName: "GL_EM", 
      width: 80,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "glStr", 
      headerName: "GL_STR", 
      width: 80,
      editable: true  // ✅ Inline editing
    },
    { 
      field: "indicator", 
      headerName: "Indicator", 
      width: 80,
      type: 'boolean',
      editable: true  // ✅ Inline editing
    },
    { 
      field: "actions", 
      headerName: "Actions", 
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => navigate(`/edit-master-item/${params.row.id}`)}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={() => {
              setWarning({
                text: "Delete this master item?",
                type: "deleteMaster",
                id: params.row.id,
              });
              setOpenWarning(true);
            }}
          >
            Delete
          </Button>
        </div>
      )
    },
  ];

  const masterRows = masterItems.map((m, idx) => ({
    id: m._id,  // MUI DataGrid requires 'id' field
    s_no: idx + 1,
    pn: m.itemPN,
    description: m.description || "",
    issueMult: m.issueMult ?? 0,
    uom: m.uom || "",
    costEach: m.costEach ?? 0,
    convFactor: m.convFactor ?? 0,
    vendorPN: m.vendorPN || "",
    expires: m.expires || null,
    procNotes: m.procNotes || "",
    commodityCode: m.commodityCode || "",
    defaultCategory: m.defaultCategory || "",
    ccGL: m.costCenters?.ccGL || "",
    ccJob: m.costCenters?.ccJob || "",
    ccWty: m.costCenters?.ccWty || "",
    ccWo: m.costCenters?.ccWo || "",
    ccCapex: m.costCenters?.ccCapex || "",
    glBm: m.costCenters?.glBm || "",
    glEm: m.costCenters?.glEm || "",
    glStr: m.costCenters?.glStr || "",
    indicator: m.indicator || false,
  }));

// Handle inline cell edits
  const handleCellEdit = async (newRow, oldRow) => {
    console.log('🔧 Cell edit triggered');
    console.log('Old row:', oldRow);
    console.log('New row:', newRow);
    
    // Find what changed
    const changedField = Object.keys(newRow).find(
      key => newRow[key] !== oldRow[key]
    );
    
    if (!changedField) {
      console.log('No changes detected');
      return oldRow;
    }
    
    console.log('🔍 Changed field:', changedField, 'New value:', newRow[changedField]);
    
    try {
      if (view === "storehouse") {
        // Update storehouse item
        const endpoint = `${process.env.REACT_APP_BASE_URL}common/items/${newRow.id}`;
        
        // Map field names back to API format
        const updateData = {};
        const value = newRow[changedField];
        
        if (changedField === 'name') updateData.itemName = value;
        else if (changedField === 'vendor') updateData.vendor = value;
        else if (changedField === 'easPN') updateData.easPN = value;
        else if (changedField === 'min') updateData.min = Number(value);
        else if (changedField === 'max') updateData.max = Number(value);
        else if (changedField === 'orderMult') updateData.orderMult = Number(value);
        else if (changedField === 'group') updateData.group = value;
        else if (changedField === 'alwaysCount') updateData.alwaysCount = value;
        else if (changedField === 'shelf') updateData.shelf = value;
        else if (changedField === 'active') updateData.active = value;
        
        console.log('📦 Update payload:', updateData);
        
        await axios.put(endpoint, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Update local state
        setStoreItems(prev => 
          prev.map(item => item._id === newRow.id ? { ...item, ...updateData } : item)
        );
        
        console.log('✅ Updated successfully');
        return newRow;
        
      } else {
        // Update master item
        const endpoint = `${process.env.REACT_APP_BASE_URL}common/master-items/${newRow.id}`;
        const value = newRow[changedField];
        
        const updateData = { [changedField]: value };
        
        // Handle nested costCenters fields
        if (['ccGL', 'ccJob', 'ccWty', 'ccWo', 'ccCapex', 'glBm', 'glEm', 'glStr'].includes(changedField)) {
          const item = masterItems.find(m => m._id === newRow.id);
          updateData.costCenters = {
            ...item.costCenters,
            [changedField]: value
          };
          delete updateData[changedField];
        }
        
        console.log('📦 Update payload:', updateData);
        
        await axios.put(endpoint, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Update local state
        setMasterItems(prev =>
          prev.map(item => item._id === newRow.id ? { ...item, ...updateData } : item)
        );
        
        console.log('✅ Updated successfully');
        return newRow;
      }
      
    } catch (err) {
      console.error('❌ Update failed:', err);
      console.error('Error details:', err.response?.data);
      alert(err.response?.data?.error || 'Failed to update item');
      // Revert by returning old row
      return oldRow;
    }
  };

  return (
    <PageLayout>
      {/* View Toggle */}
      <Grid container spacing={2} className="mb-2 align-items-center">
        <Grid size={{ xs: 12, md: 6 }}>
          <Breadcrumb title={view === "storehouse" ? "Storehouse Items Management" : "All Items Management"} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} className="text-end">
          <Button
            variant={view === "storehouse" ? "contained" : "outlined"}
            color="primary"
            className="me-2"
            onClick={() => setView("storehouse")}
          >
            Storehouse Items
          </Button>
          <Button
            variant={view === "master" ? "contained" : "outlined"}
            color="primary"
            onClick={() => setView("master")}
          >
            Master Items
          </Button>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      {view === "storehouse" ? (
        <Grid container spacing={2} className="mb-3 align-items-center">
          <Grid size={12}>
            <Link to="/add-item">
              <Button variant="contained" color="primary" className="me-2">
                Add Storehouse Item
              </Button>
            </Link>
            <a href="/templates/storehouse_sample.csv" download>
              <Button variant="outlined" className="me-2">
                Download Storehouse Sample
              </Button>
            </a>
            <Button
              variant="outlined"
              onClick={() => storeFileRef.current.click()}
            >
              Upload Storehouse CSV
            </Button>
            <input
              type="file"
              accept=".csv"
              ref={storeFileRef}
              style={{ display: "none" }}
              onChange={onStoreFileChange}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2} className="mb-3 align-items-center">
          <Grid size={12}>
            <Link to="/add-master-item">
              <Button variant="contained" color="primary" className="me-2">
                Add Master Item
              </Button>
            </Link>
            <a href="/templates/master_sample.csv" download>
              <Button variant="outlined" className="me-2">
                Download Master Sample
              </Button>
            </a>
            <Button
              variant="outlined"
              onClick={() => masterFileRef.current.click()}
            >
              Upload Master CSV
            </Button>
            <input
              type="file"
              accept=".csv"
              ref={masterFileRef}
              style={{ display: "none" }}
              onChange={onMasterFileChange}
            />
          </Grid>
        </Grid>
      )}

      {/* Data Table */}
      <Box className="mc-card">
        <CardHeader
          title={view === "storehouse" ? "Storehouse Items" : "Master Items"}
        />

        <div style={{ height: 600, width: '100%', marginTop: '1rem', minWidth: 0 }}>
            <DataGrid
              rows={view === "storehouse" ? storehouseRows : masterRows}
              columns={view === "storehouse" ? storehouseColumns : masterColumns}
              pageSize={25}
              rowsPerPageOptions={[25, 50, 100]}
              disableSelectionOnClick
              experimentalFeatures={{ newEditingApi: true }}
              processRowUpdate={handleCellEdit}
              onProcessRowUpdateError={(error) => {
                console.error('Row update error:', error);
              }}
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: '2px solid #1976d2',
                },
              }}
            />
        </div>
      </Box>

      {/* Confirmation Modal */}
      <Modal
        open={openWarning}
        onClose={() => setOpenWarning(false)}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <Sheet
          variant="outlined"
          sx={{
            width: "30%",
            p: 3,
            borderRadius: "md",
            boxShadow: "lg",
            bgcolor: "#fff",
          }}
        >
          <ModalClose sx={{ top: 8, right: 8 }} />
          <Typography fontSize="1.25em" mb={3}>
            {warning.text}
          </Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "2rem",
            }}
          >
            <Button
              variant="contained"
              color="success"
              style={{ width: "8rem" }}
              onClick={() => {
                if (warning.type === "deleteStore") {
                  deleteStoreItem(warning.id);
                } else if (warning.type === "statusStore") {
                  toggleStoreStatus(warning.id);
                } else if (warning.type === "deleteMaster") {
                  deleteMasterItem(warning.id);
                }
                setOpenWarning(false);
              }}
            >
              Yes
            </Button>
            <Button
              variant="contained"
              color="error"
              style={{ width: "8rem" }}
              onClick={() => setOpenWarning(false)}
            >
              No
            </Button>
          </div>
        </Sheet>
      </Modal>
    </PageLayout>
  );
}
